import { UserTable } from "@/routes/_dashboard/users/-components/user-table";
import { organisationUsersQueryOptions } from "@/server/users";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useContext, useEffect, useMemo, useState } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { SidebarUser } from "@/routes/_dashboard/users/-components/user-sidebar";
import { minuteCounterOverridesQueryOptions } from "@/server/settings";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/users")({
  component: RouteComponent,
  loader: async ({ params, context }) => {
    return {
      orgId: params.orgId,
      crumb: "Users",
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useParams();
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    expire?: number;
    id?: string;
  };
  const queryClient = useQueryClient();

  // Parse expire from searchParams if it's a string date
  const expire = useMemo(() => {
    if (typeof searchParams.expire === "number") {
      return searchParams.expire;
    }
    if (searchParams.expire) {
      const expireDate = new Date(searchParams.expire);
      if (!isNaN(expireDate.getTime())) {
        return parseInt(expireDate.getTime().toFixed(0));
      }
    }
    return undefined;
  }, [searchParams.expire]);

  const organisationUsersQueryOptionsMemo = useMemo(
    () => organisationUsersQueryOptions(Number(orgId ?? 0), searchParams.search, expire),
    [orgId, searchParams.search, expire]
  );
  const organisationUsersQuery = useQuery(organisationUsersQueryOptionsMemo);
  const organisationUsers = organisationUsersQuery.data;
  const minuteCounterOverridesQueryOptionsMemo = useMemo(() => minuteCounterOverridesQueryOptions, []);
  const minuteCounterOverridesQuery = useQuery(minuteCounterOverridesQueryOptionsMemo);
  const minuteCounterOverrides = minuteCounterOverridesQuery.data?.minuteCounterOverrides;
  const navigate = useNavigate();
  const context = useContext(DashboardContext);
  const [isContextReady, setIsContextReady] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    void organisationUsersQuery.refetch();
    void minuteCounterOverridesQuery.refetch();
  }, [orgId]);

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  const handleAddUser = () => {
    navigate({
      to: "/organisations/$orgId/users",
      params: { orgId: orgId ?? 0 },
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  const handleUserCreated = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(orgId ?? 0);
      },
    });
    closeAfterSave();
  };

  const handleUserUpdated = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(orgId ?? 0);
      },
    });
    closeAfterSave();
  };

  const closeAfterSave = () => {
    context?.setRightSidebar(null);
    navigate({
      to: "/organisations/$orgId/users",
      params: { orgId: orgId ?? 0 },
      replace: true,
      search: (prev) => {
        const next = { ...(prev || {}) } as Record<string, unknown>;
        delete next.id;
        delete next.tab;
        delete next.transactionsPage;
        delete next.transactionsPerPage;
        delete next.transactionsSortBy;
        delete next.transactionsSortDir;
        return Object.keys(next).length > 0 ? next : undefined;
      },
    });
  };

  const handleUserDeleted = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(orgId ?? 0);
      },
    });
    context?.setRightSidebar(null);
    navigate({
      to: "/organisations/$orgId/users",
      params: { orgId: orgId ?? 0 },
      replace: true,
      search: {},
    });
  };

  useEffect(() => {
    if (!isContextReady || !context?.setRightSidebar) {
      return;
    }

    if (searchParams.id && searchParams.id !== "new" && organisationUsers && organisationUsers.length) {
      const user = organisationUsers.find((u) => u.id === searchParams.id);
      if (user) {
        context.setRightSidebar(
          <SidebarUser
            userId={Number(user.id)}
            organisationId={Number(orgId ?? 0)}
            onUserUpdated={handleUserUpdated}
            onUserDeleted={handleUserDeleted}
            isOrganisationRouteUser={true}
            minuteCounterOverrides={minuteCounterOverrides}
            closeAfterSave={closeAfterSave}
          />
        );
        context.setRightSidebarParamKey?.([
          "id",
          "tab",
          "transactionsPage",
          "transactionsPerPage",
          "transactionsSortBy",
          "transactionsSortDir",
        ]);
      }
      return;
    }

    if (searchParams.id === "new") {
      context.setRightSidebar(
        <SidebarUser
          userId={null}
          organisationId={Number(orgId ?? 0)}
          onUserCreated={handleUserCreated}
          isOrganisationRouteUser={true}
          minuteCounterOverrides={minuteCounterOverrides}
          closeAfterSave={closeAfterSave}
        />
      );
      context.setRightSidebarParamKey?.([
        "id",
        "tab",
        "transactionsPage",
        "transactionsPerPage",
        "transactionsSortBy",
        "transactionsSortDir",
      ]);
      return;
    }
    //Clear sidebar if no user selected.
    context.setRightSidebar(null);
    context.setRightSidebarParamKey?.(undefined);
  }, [searchParams.id, isContextReady, organisationUsers]);

  useEffect(() => {
    return () => {
      if (context) {
        context.setRightSidebar(null);
        context.setRightSidebarParamKey?.(undefined);
      }
    };
  }, []);

  return (
    <UserTable
      users={organisationUsers}
      organisationId={Number(orgId ?? 0)}
      handleAddUser={handleAddUser}
      isLoading={organisationUsersQuery.isLoading || organisationUsersQuery.isFetching}
    />
  );
}
